import { save as safeSave } from '../../../lib/safeData';
/**
 * Handles the authentication process with Olapic credentials and redirects the user to 3rd
 * party providers login screens.
 */
class LoginContainer {
  /**
   * @param {ngLocation}            $location         If the user logs in with Olapic credentials,
   *                                                  this service is used to redirect the browser
   *                                                  to the success screen, so the token can be
   *                                                  sent to the SSO Client implementation.
   * @param {ngQ}                   $q                To create an already rejected promise. This
   *                                                  is used when the authentication request is
   *                                                  made while another is already being
   *                                                  processed.
   * @param {ngTimeout}             $timeout          To defer redirections on events.
   * @param {Window}                $window           This is used to redirect the browser if the
   *                                                  user wants to log in with an SSO provider.
   *                                                  `$location` can't be used as the URL is
   *                                                  external to the app.
   * @param {FrontendConfiguration} appConfiguration  To get the Olapic's privacy policy URL.
   * @param {AppErrorHandler}       appErrorHandler   To silently handle the error in the case the
   *                                                  request for the app/scope the screen uses as
   *                                                  _"subtitle"_ fails.
   * @param {Auth}                  auth              To make the request to authenticate a user.
   * @param {AuthRoutesManager}     authRoutesManager To get the routes for the _"success"_ and
   *                                                  "remember my password"_ routes.
   * @param {SSOClient}             ssoClient         To watch the cookie in case it gets created
   *                                                  before the user is authenticated (in case
   *                                                  he/she logs in on a different browser tab).
   * @param {SSOErrors}             ssoErrors         In the case the container recevies an error
   *                                                  via binding, this service will be used to
   *                                                  get the error description message.
   * @param {SSOState}              ssoState          To get the settings of the SSO client
   *                                                  implementation.
   * @param {SSOStorage}            ssoStorage        To check if there's an email address already
   *                                                  saved.
   */
  constructor(
    $location,
    $q,
    $timeout,
    $window,
    appConfiguration,
    appErrorHandler,
    auth,
    authRoutesManager,
    ssoClient,
    ssoErrors,
    ssoState,
    ssoStorage,
  ) {
    'ngInject';

    /**
     * A local reference for the `$location` service.
     * @type {ngLocation}
     */
    this.$location = $location;
    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `$timeout` service.
     * @type {ngTimeout}
     */
    this.$timeout = $timeout;
    /**
     * A local reference for the browser window.
     * @type {Window}
     */
    this.$window = $window;
    /**
     * A local reference for the `appErrorHandler` service.
     * @type {AppErrorHandler}
     */
    this.appErrorHandler = appErrorHandler;
    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesManager}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * Redirect to manifest login.
     * @type {string}
     */
    this.manifestRedirectEnabled = appConfiguration.features.manifestRedirect.manifestRedirectEnabled;
    /**
     * Manifest login URL.
     * @type {string}
     */
    this.manifestRedirectUrl = appConfiguration.features.manifestRedirect.manifestRedirectUrl;
    /**
     * A local reference for the `ssoClient` service.
     * @type {SSOClient}
     */
    this.ssoClient = ssoClient;
    /**
     * A local reference for the `ssoErrors` service.
     * @type {SSOErrors}
     */
    this.ssoErrors = ssoErrors;
    /**
     * A local reference for the `ssoState` service.
     * @type {SSOState}
     */
    this.ssoState = ssoState;
    /**
     * A local reference for the `ssoStorage` service.
     * @type {SSOStorage}
     */
    this.ssoStorage = ssoStorage;
    /**
     * The Privacy policy URL, to show on a link.
     * @type {string}
     */
    this.privacyPolicyUrl = appConfiguration.privacyPolicyURL;
    /**
     * The Request a demo URL, to show on a link.
     * @type {string}
     */
    this.requestDemoUrl = appConfiguration.requestDemoURL;
    /**
     * The URL for the _"Remember my password"_ link.
     * @type {string}
     */
    this.rememberPasswordUrl = this.authRoutesManager.rememberPassword(true);
    /**
     * Once the creadentials are submitted and validated, in case of error, this property will be
     * used to store the message and send it to the child component.
     * @type {string}
     */
    this.error = '';
    /**
     * The name of the application the user is being authenticated for. When the component is
     * mounted, if the SSO client sent an application ID, the application information will be
     * requested and its name will be the subtitle; but if the user used a permission scope,
     * that scope name name will be the subtitle.
     * It's called subtitle because the child component will show it under the Olapic's logo.
     * @type {string}
     */
    this.subtitle = '';
    /**
     * Whether or not the container is processing the credentials.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * Whether or not the user already entered his/her email address. This is used to inform
     * the child component that it shouldn't show the email input anymore.
     * @type {boolean}
     */
    this.emailReady = false;
    /**
     * Whether or not the user can login using credentials (email and password). This value gets
     * updated after the user enter his/her email address and the login settings for her/him are
     * loaded.
     * @type {Boolean}
     */
    this.credentialsEnabled = false;
    /**
     * The list of SSO providers avaiable for the user. This value gets updated after the user
     * enter his/her email address and the login settings for her/him are loaded.
     * @type {Array}
     */
    this.ssoProviders = [];
    /**
     * If the email address is preloaded, this property will be used to save it; so
     * it can be sent to the component and rendered as the default value for the form.
     * @type {string}
     */
    this.initialEmailAddress = '';
    /**
     * If the email address is preloaded, this flag will be set to `true` and it will prevent the
     * container from requesting the email address information twice when the user submits the
     * form.
     * @type {boolean}
     * @access protected
     * @ignore
     */
    this._preloaded = false;
  }
  /**
   * When the container gets mounted, it will first check if there's an error from a previous
   * authentication attempt, then it will obtain the list of 3rd party providers and load the
   * information from either the app or scope the implementation uses to set as subtitle.
   */
  // eslint-disable-next-line complexity
  $onInit() {
    const queryString = this.$window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const redirectManifestParam = urlParams.get('redirect_manifest');
    // eslint-disable-next-line angular/log, no-console
    console.debug('Redirect manifest param ===>', redirectManifestParam);

    if (!redirectManifestParam || redirectManifestParam === 'true') {
      // Does not have the &redirect_manifest=false param
      // Check if default_manifest_redirect is enabled
      // eslint-disable-next-line angular/log, no-console
      console.debug('Manifest redirection enabled config ===>', this.manifestRedirectEnabled);
      // eslint-disable-next-line angular/log, no-console
      console.debug('Manifest redirection UR ===>', this.manifestRedirectUrl);
      if (this.manifestRedirectEnabled) {
        this.$window.location = this.manifestRedirectUrl;
      }
    }

    if (this.errorCode) {
      this.error = this.ssoErrors.getErrorDescription(this.errorCode);
    }
    if (!this.state && !this.error) {
      /**
       * If there's no state and no error was received, which means that this authentication
       * process wasn't started by the SSO client, create a state with the fallback implementation.
       */
      this.ssoState.createDefaultState();
    }

    // Get the state contents.
    let state = this.ssoState.getState();
    /**
     * If, for some reason, the flag said there was an state but there wasn't... or if the
     * current state doesn't have an authentication method, generate a new state.
     */
    if (!state || (!state.application && !state.accountScope)) {
      this.ssoState.createDefaultState();
      state = this.ssoState.getState();
    }

    // Try to get a saved email address in order to set it as the form default one.
    const savedEmail = this.ssoStorage.getUserEmailAddress();

    // Define the first step of the promise chain.
    let firstStep;
    if (state.application) {
      // If the implementation sent an application name, use that application title as subtitle.
      firstStep = this._loadApplication(state.application, !savedEmail);
    } else if (state.accountScope) {
      // If the implementation sent a permissions scope, use that scope title as subtitle.
      firstStep = this._loadScope(state.accountScope, !savedEmail);
    }
    /**
     * If there was an email address, after getting the app/scope information, load its email
     * information.
     */
    if (savedEmail) {
      firstStep.then(() => this._restoreEmailAddress(savedEmail));
    }

    // Set a listener in case the cookie gets created by another implementation.
    this.ssoClient.onSessionCreated(() => {
      // Avoid triggering the redirection if the application just finished creating the cookie.
      if (!this.ssoClient.finished) {
        // Get the token and validate to make sure a valid cookie was created.
        const token = this.ssoClient.getToken();
        if (token) {
          // Redirect to the success screen.
          this.$timeout(() => {
            this.$location.url(this.authRoutesManager.success(safeSave(token)));
          });
        }
      }
    });
  }
  /**
   * Processes the login with Olapic credentials. This is the callback sent to the child component.
   * @param {string} email    The user email address.
   * @param {string} password The user password.
   * @return {Promise<undefined,Error>}
   */
  doLogin(email, password) {
    let result;
    if (this.loading) {
      // Reject the promise if the container is already processing the credentials.
      result = this.$q.reject(new Error('The login is in progress'));
    } else {
      // Reset any possible error.
      this.error = '';
      // Turn on the _"processing flag"_.
      this.loading = true;
      // Authenticate the user.
      result = this.auth.login(email, password)
      .then((userData) => {
        // If the login was a success, redirect the user to the success page.
        this.$location.url(this.authRoutesManager.success(safeSave(userData.token)));
      })
      .catch((error) => {
        // If there was an error, capture its message.
        let errorMessage = error.message;
        /**
         * If the error message is that the user was locked out due to multiple attemps to login
         * with incorrect credentials, format it. (QA approved).
         */
        if (errorMessage.match(/locked/i)) {
          [errorMessage] = errorMessage.split('.');
        }
        // Set the error message on the local property.
        this.error = errorMessage;
      })
      .then(() => {
        // Turn off the _"processing flag"_.
        this.loading = false;
      });
    }
    // Return the promise.
    return result;
  }
  /**
   * This is called when the user enters his/her email address; it loads the list of avaiable
   * SSO providers and whether or not she/he can login user credentials (email and password).
   * @param {string}  email                   The user email address.
   * @param {boolean} [forVerification=false] Whether or not the method is just being used to
   *                                          verify the email address information. When this flag
   *                                          is set to `true`, no SSO provider will be
   *                                          auto-selected and the loading flag won't be changed
   *                                          to false when the process finishes.
   * @return {Promise<undefined,Error>}
   */
  loadSSOProvidersInformation(email, forVerification = false) {
    this.error = '';
    this.loading = true;
    let firstStep;
    /**
     * If the `_preloaded` flag is _"on"_ and the saved addres is the same as the one for which
     * the information needs to be requested for...
     */
    if (this._preloaded && email === this.initialEmailAddress) {
      // ...set an alrady resolved promise as first step; the inform is already loaded.
      firstStep = this.$q.resolve();
    } else {
      // ...make request.
      firstStep = this.auth.getProvidersInformation(email)
      .then(({ providers, olapicAccountEnabled, olapicHomeEnabled }) => {
        this.credentialsEnabled = olapicAccountEnabled || !providers.length;
        // If the Home is enabled, update the state.
        if (olapicHomeEnabled) {
          this.ssoState.useHomeState();
        }
        // Saved the recevied list of providers.
        this.ssoProviders = providers;
      });
    }
    // Turn _"off"_ the flag.
    this._preloaded = false;
    return firstStep
    .then(() => {
      // If there's only one provider, select it.
      if (
        !forVerification &&
        this.ssoProviders.length === 1 &&
        !this.credentialsEnabled
      ) {
        const [firstProvider] = this.ssoProviders;
        this.selectSSOProvider(firstProvider);
      }
    })
    .then(() => {
      /**
       * Only turn off the _"processing flag"_ when there are no providers or more than one.
       * The reason the flag doesn't change when there's only one provider is because the browser
       * will be redirected, so instead of showing the form for a second and then redirect, we
       * keep the flag on, like it's processing, and then redirect the browser.
       */
      if (
        !forVerification &&
        (this.credentialsEnabled || this.ssoProviders.length !== 1)
      ) {
        // Turn off the _"processing flag"_.
        this.loading = false;
      }
    });
  }
  /**
   * If the method receives a valid email address, it loads the SSO providers and login options
   * for the user email address; but if the address is an empty string, it resets everything.
   * @param {string} [email] The user email address.
   */
  saveEmailAddress(email) {
    if (email) {
      this.emailReady = true;
      this.loadSSOProvidersInformation(email)
      .catch((error) => {
        this.appErrorHandler.handle(error, 'There was an error loading the information');
      });
    } else {
      this.ssoStorage.deleteUserEmailAddress();
      this.emailReady = false;
      this.ssoProviders = [];
      this.credentialsEnabled = false;
    }
  }
  /**
   * This is called when the user selects an SSO provider or when there's only one avaiable.
   * It redirects the browser to the provider URL.
   * @param {SSOProvider} provider The selected provider information.
   */
  selectSSOProvider(provider) {
    this.$window.location.href = provider.url;
  }
  /**
   * Loads an application information and use its title as subtitle.
   * @param {string}  name                       The application name.
   * @param {boolean} turnOffLoadingAfterRequest Wheter or not to set the _"loading flag"_ to
   *                                             `false` after the information is savd.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _loadApplication(name, turnOffLoadingAfterRequest) {
    return this._loadSubtitle(
      () => this.auth.getApplication(name),
      turnOffLoadingAfterRequest,
    );
  }
  /**
   * Loads a permissions scope and use its title as subtitle.
   * @param {string}  scope                      The name of the scope.
   * @param {boolean} turnOffLoadingAfterRequest Wheter or not to set the _"loading flag"_ to
   *                                             `false` after the information is savd.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _loadScope(scope, turnOffLoadingAfterRequest) {
    return this._loadSubtitle(
      () => this.auth.getScope(scope),
      turnOffLoadingAfterRequest,
    );
  }
  /**
   * Loads an entity (application or scope) using a callback function, gets its `title` property
   * and set it as the `subtitle` property.
   * @param {Function} reqFn                     The function that once called will return a
   *                                             promise to be resolved with the entity
   *                                             information.
   * @param {boolean} turnOffLoadingAfterRequest Wheter or not to set the _"loading flag"_ to
   *                                             `false` after the information is savd.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _loadSubtitle(reqFn, turnOffLoadingAfterRequest) {
    // Turn on the _"processing flag"_.
    this.loading = true;
    // Call the function that will return the entity promise.
    return reqFn()
    .then((response) => {
      // Get the entity `title` and save it on `subtitle`.
      this.subtitle = response.title;
    })
    .catch((error) => {
      /**
       * Since this is only used to display a subtitle, if the request fails, don't show the
       * error message to the user, just tell the error handler to silently log it on the
       * console.
       */
      this.appErrorHandler.silent(error);
    })
    .then(() => {
      // Turn off the _"processing flag"_ if needed.
      if (turnOffLoadingAfterRequest) {
        this.loading = false;
      }
    });
  }
  /**
   * This is called when the container finds an email address to restore and set as default
   * value for the form. It simply loads the email information and decides whether to move the
   * user to the second screen or not.
   * @param {string} email The default email address.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _restoreEmailAddress(email) {
    return this.loadSSOProvidersInformation(email, true)
    .then(() => {
      this._preloaded = true;
      this.initialEmailAddress = email;
      if (this.credentialsEnabled || this.ssoProviders.length > 1) {
        this.saveEmailAddress(email);
      }
    })
    .catch((error) => {
      // If something fails, restore all the important flags and handle the error silently.
      this._preloaded = false;
      this.initialEmailAddress = false;
      this.emailReady = false;
      this.appErrorHandler.silent(error);
    })
    .then(() => {
      // Finally, turn off the _"processing flag"_.
      this.loading = false;
    });
  }
}
/**
 * Shows and handles the logic of the login screen, no matter if the user logs in using Olapic
 * credentials of if he/she chooses to use a 3rd party provider.
 * @ngesdoc component
 * @ngname loginContainer
 * @ngcontroller {@link LoginContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {?boolean} state       Whether a state is currently saved or not. This is `true` when
 *                                   the user arrives to this container after the SSO client
 *                                   parameters were processed; and `false` if the user just wrote
 *                                   the URL of the app.
 * @ngbinding {?string}  errorCode   An error code returned by the server after trying to
 *                                   authenticate a user.
 */
export default {
  controller: LoginContainer,
  template: `
    <login
      initial-email="$ctrl.initialEmailAddress"
      subtitle="$ctrl.subtitle"
      error="$ctrl.error"
      loading="$ctrl.loading"
      email-ready="$ctrl.emailReady"
      credentials-enabled="$ctrl.credentialsEnabled"
      forgot-password-url="$ctrl.rememberPasswordUrl"
      privacy-policy-url="$ctrl.privacyPolicyUrl"
      request-demo-url="$ctrl.requestDemoUrl"
      providers="$ctrl.ssoProviders"
      on-save-email-address="$ctrl.saveEmailAddress(email)"
      on-login-with-credentials="$ctrl.doLogin(email, password)"
      on-login-with-provider="$ctrl.selectSSOProvider(provider)"
    ></login>
  `,
  bindings: {
    state: '<',
    errorCode: '@',
  },
};
